/**
 * A lista das rotas que podem ser indexadas, e tudo o que se deriva dela.
 *
 * Fonte ÚNICA: o `<title>`, a `description`, o `canonical`, o `noindex`, o `robots.txt` e o
 * `sitemap.xml` saem todos daqui. Duas listas divergiriam — a rota entraria no sitemap e continuaria
 * `noindex`, ou sairia do sitemap e seguiria indexável.
 *
 * FECHADA POR PADRÃO: rota que não está no mapa é `noindex,nofollow` e fica fora do sitemap. É o que
 * faz uma tela nova nascer fora do índice sem ninguém precisar lembrar de atualizar lista nenhuma —
 * e hoje 105 das 107 rotas do app são telas privadas.
 */

/**
 * Absoluta e fixa no código, não em env var: prod não tem environment no GitHub, e uma variável
 * faltando viraria string vazia no build da imagem — o canonical sairia quebrado em todas as
 * páginas públicas sem nada acusar.
 */
export const SITE_URL = 'https://app.vibx.com.br';

/** O título que as telas privadas continuam servindo. Elas são `noindex`, então não disputam nada. */
export const TITULO_PADRAO = 'VibX - Área do Criador';

type MetadadosPublicos = {
  title: string;
  description: string;
};

/**
 * `Map`, e não objeto literal: com objeto, `ROTAS_INDEXAVEIS['__proto__']` devolveria algo herdado
 * do protótipo e uma rota inexistente passaria por indexável. `Map` só responde ao que foi posto
 * nele.
 */
export const ROTAS_INDEXAVEIS = new Map<string, MetadadosPublicos>([
  [
    '/auth/signIn',
    {
      /*
        "Login" no título, e não só "Entrar": a busca que se quer captar é `vibx login` — 320/mês,
        crescendo 50% no trimestre, e hoje o VibX aparece em 3% delas. O casamento exato no título é
        dos sinais mais fortes, e é o texto que aparece no resultado.
      */
      title: 'Login VibX — Entrar na Área do Criador',
      description:
        'Faça login na VibX e acesse a Área do Criador para gerenciar suas comunidades, vendas e assinaturas no Telegram.',
    },
  ],
  [
    '/auth/register',
    {
      title: 'Criar conta na VibX — Cadastro de Criador',
      description:
        'Crie sua conta VibX e comece a vender conteúdo e assinaturas por bot do Telegram na Área do Criador.',
    },
  ],
]);

export type MetadadosDaRota = {
  title: string;
  description?: string;
  canonical?: string;
  indexavel: boolean;
};

/**
 * O que o `<head>` daquela rota deve dizer.
 *
 * `canonical` só nas indexáveis, de propósito: `pathname` numa rota dinâmica é o padrão cru
 * (`/products/[productCode]`), e um canonical apontando para isso seria uma URL que não existe.
 */
export const metadadosDaRota = (pathname: string): MetadadosDaRota => {
  const publicos = ROTAS_INDEXAVEIS.get(pathname);

  if (!publicos) return { title: TITULO_PADRAO, indexavel: false };

  return {
    ...publicos,
    canonical: `${SITE_URL}${pathname}`,
    indexavel: true,
  };
};

export const urlsIndexaveis = () =>
  Array.from(ROTAS_INDEXAVEIS.keys(), (pathname) => `${SITE_URL}${pathname}`);

/**
 * Bloqueia só `/api/`.
 *
 * Um `Disallow: /` geral pareceria mais seguro e faria o oposto do que se quer: página bloqueada no
 * robots nunca é rastreada, então o `noindex` dela nunca é lido — e URL bloqueada segue aparecendo
 * no índice, só que sem descrição. Quem tira as telas privadas do índice é o `noindex`, e para ele
 * ser lido o rastreio precisa estar liberado.
 */
export const corpoDoRobotsTxt = () =>
  [
    'User-agent: *',
    'Disallow: /api/',
    '',
    `Sitemap: ${SITE_URL}/sitemap.xml`,
    '',
  ].join('\n');

export const corpoDoSitemapXml = () =>
  [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...urlsIndexaveis().map((url) => `  <url><loc>${url}</loc></url>`),
    '</urlset>',
    '',
  ].join('\n');
